import { Component, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FieldsetModule } from 'primeng/fieldset';
import { DropdownModule } from 'primeng/dropdown';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TooltipModule } from 'primeng/tooltip';
import { CatalogoOption } from 'src/app/domain/model/catalogo/catalogo';
import { EmpleadoRequest } from 'src/app/domain/model/empleado/empleado';
import { YES_VALUE, NO_VALUE } from 'src/app/common/constants-ejb';

/**
 * Dumb component: "Información Movilidad Consultor" de "Modificar
 * Empleado/Consultor" (HU1259115).
 *
 * <p>Legacy: `panelMovilidadAsesor` (`ModificarEmpleadoViewImpl.ui.xml`) —
 * sección propia con título `{cons.panelMovilidadAsesorTitle}` ("Información
 * Movilidad Consultor"), visible solo cuando el tipo de empleado es Asesor
 * exacto (`inicializarPanelTte(isAsesor)`), no el grupo más amplio
 * Asesor/DirTecnico/AsesorLineaAt de "Datos Consultor". Antes de esta fase
 * (F23 del plan de "Modificar Empleado") estos 5 campos vivían fusionados
 * dentro de "Datos Consultor" — se restauran aquí como sección
 * independiente para respetar el orden/agrupación visual del legado.</p>
 *
 * <p>`[(ngModel)]` muta directamente el objeto `form` recibido por
 * referencia — el padre y el facade comparten la misma instancia, así que no
 * hace falta un `@Output()` por campo (patrón ya usado en el resto del
 * formulario, ver Javadoc de `ModificarEmpleadoComponent`).</p>
 */
@Component({
  selector: 'app-movilidad-consultor',
  standalone: true,
  imports: [CommonModule, FormsModule, FieldsetModule, DropdownModule, RadioButtonModule, TooltipModule],
  template: `
    <p-fieldset legend="Información Movilidad Consultor" [toggleable]="true" styleClass="mb-4">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 sm:gap-x-6 gap-y-3">
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tablet *</label>
          <div class="flex flex-wrap gap-4 pt-2">
            <div class="flex items-center">
              <p-radioButton name="tablet" [value]="YES" [(ngModel)]="form().sntablet" inputId="tabletSi" pTooltip="Seleccione si transporta tablet"></p-radioButton>
              <label for="tabletSi" class="ml-2 text-sm">Sí</label>
            </div>
            <div class="flex items-center">
              <p-radioButton name="tablet" [value]="NO" [(ngModel)]="form().sntablet" inputId="tabletNo" pTooltip="Seleccione si transporta tablet"></p-radioButton>
              <label for="tabletNo" class="ml-2 text-sm">No</label>
            </div>
          </div>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Módem *</label>
          <div class="flex flex-wrap gap-4 pt-2">
            <div class="flex items-center">
              <p-radioButton name="modem" [value]="YES" [(ngModel)]="form().snmodem" inputId="modemSi" pTooltip="Seleccione si transporta módem"></p-radioButton>
              <label for="modemSi" class="ml-2 text-sm">Sí</label>
            </div>
            <div class="flex items-center">
              <p-radioButton name="modem" [value]="NO" [(ngModel)]="form().snmodem" inputId="modemNo" pTooltip="Seleccione si transporta módem"></p-radioButton>
              <label for="modemNo" class="ml-2 text-sm">No</label>
            </div>
          </div>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Computador Portátil *</label>
          <div class="flex flex-wrap gap-4 pt-2">
            <div class="flex items-center">
              <p-radioButton name="computador" [value]="YES" [(ngModel)]="form().sncomputador" inputId="computadorSi" pTooltip="Seleccione si transporta computador"></p-radioButton>
              <label for="computadorSi" class="ml-2 text-sm">Sí</label>
            </div>
            <div class="flex items-center">
              <p-radioButton name="computador" [value]="NO" [(ngModel)]="form().sncomputador" inputId="computadorNo" pTooltip="Seleccione si transporta computador"></p-radioButton>
              <label for="computadorNo" class="ml-2 text-sm">No</label>
            </div>
          </div>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tipo de Asignación *</label>
          <p-dropdown [(ngModel)]="form().dstipoAsignacion" [options]="opcionesTipoAsignacion()"
            optionLabel="descripcion" optionValue="codigo" placeholder="--Seleccione--"
            appendTo="body" [style]="{ width: '100%' }" pTooltip="Seleccione el tipo de asignación"></p-dropdown>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Celular *</label>
          <div class="flex flex-wrap gap-4 pt-2">
            <div class="flex items-center">
              <p-radioButton name="celular" [value]="YES" [(ngModel)]="form().sncelular" inputId="celularSi" pTooltip="Seleccione si transporta celular"></p-radioButton>
              <label for="celularSi" class="ml-2 text-sm">Sí</label>
            </div>
            <div class="flex items-center">
              <p-radioButton name="celular" [value]="NO" [(ngModel)]="form().sncelular" inputId="celularNo" pTooltip="Seleccione si transporta celular"></p-radioButton>
              <label for="celularNo" class="ml-2 text-sm">No</label>
            </div>
          </div>
        </div>
      </div>
    </p-fieldset>
  `,
})
export class MovilidadConsultorComponent {
  readonly form = input.required<EmpleadoRequest>();
  readonly opcionesTipoAsignacion = input.required<CatalogoOption[]>();

  protected readonly YES = YES_VALUE;
  protected readonly NO = NO_VALUE;
}
